// frontend/src/services/airport.service.ts
// OpenFlights Real-time Airport Search Service for React
// Data Source: https://github.com/jpatokal/openflights (Public Domain)
// Contains 7,000+ airports worldwide with IATA codes
// PHOENICIAN TECHNICAL SERVICES LLC

/// Airport model with all necessary fields
export interface Airport {
    id: number;
    name: string;
    city: string;
    country: string;
    iataCode: string;
    icaoCode: string;
    latitude: number;
    longitude: number;
    timezone: string;
  }
  
  // ============================================================================
  // PRIORITY AIRPORT CODES (for quick access and sorting)
  // ============================================================================
  
  /// UAE airport IATA codes (highest priority for departure)
  export const UAE_AIRPORT_CODES = ['DXB', 'AUH', 'SHJ', 'DWC', 'RKT', 'FJR', 'AAN'];
  
  /// GCC airport IATA codes (high priority)
  export const GCC_AIRPORT_CODES = ['DOH', 'KWI', 'BAH', 'MCT', 'RUH', 'JED', 'DMM', 'MED'];
  
  /// Popular destination codes (for suggestions)
  export const POPULAR_DESTINATION_CODES = [
    // South Asia
    'DEL', 'BOM', 'BLR', 'MAA', 'HYD', 'CCU', 'COK', 'TRV', 'CCJ', 'AMD',
    'KHI', 'LHE', 'ISB', 'DAC', 'CMB', 'KTM', 'MLE',
    // Southeast Asia
    'BKK', 'SIN', 'KUL', 'MNL', 'CGK', 'DPS', 'SGN', 'HAN',
    // East Asia
    'HKG', 'PEK', 'PVG', 'ICN', 'NRT', 'HND', 'TPE',
    // Europe
    'LHR', 'CDG', 'FRA', 'AMS', 'IST', 'FCO', 'MAD', 'BCN', 'MUC', 'ZRH',
    // Middle East
    'CAI', 'AMM', 'BEY', 'TLV',
    // Africa
    'JNB', 'NBO', 'ADD', 'LOS', 'ACC',
    // Americas
    'JFK', 'LAX', 'ORD', 'SFO', 'MIA', 'YYZ', 'GRU',
    // Oceania
    'SYD', 'MEL', 'AKL',
  ];
  
  // ============================================================================
  // AIRPORT SERVICE CLASS
  // ============================================================================
  
  class AirportService {
    private airports: Airport[] = [];
    private isLoaded = false;
    private isLoading = false;
  
    // OpenFlights data URL (free, no API key needed)
    private readonly DATA_URL = 'https://raw.githubusercontent.com/jpatokal/openflights/master/data/airports.dat';
  
    // LocalStorage cache keys
    private readonly CACHE_KEY = 'airports_cache';
    private readonly CACHE_TIMESTAMP_KEY = 'airports_cache_timestamp';
    private readonly CACHE_DURATION_DAYS = 30; // Refresh cache every 30 days
  
    // ============================================================================
    // DATA LOADING METHODS
    // ============================================================================
  
    /**
     * Load airport data (from cache or network)
     */
    async loadAirports(forceRefresh = false): Promise<void> {
      if (this.isLoaded && !forceRefresh) {
        console.log(`✅ Airports already loaded: ${this.airports.length}`);
        return;
      }
  
      if (this.isLoading) {
        console.log('⏳ Already loading airports...');
        // Wait for current loading to complete
        return new Promise((resolve) => {
          const checkLoading = setInterval(() => {
            if (!this.isLoading) {
              clearInterval(checkLoading);
              resolve();
            }
          }, 100);
        });
      }
  
      this.isLoading = true;
  
      try {
        console.log('🔄 Loading airport data...');
  
        // Try to load from cache first
        if (!forceRefresh) {
          const cachedData = this.loadFromCache();
          if (cachedData && cachedData.length > 0) {
            this.airports = cachedData;
            this.isLoaded = true;
            this.isLoading = false;
            console.log(`✅ Loaded ${this.airports.length} airports from cache`);
            return;
          }
        }
  
        // Fetch from network
        await this.fetchFromNetwork();
  
      } catch (e) {
        console.error('❌ Error loading airports:', e);
  
        // Try loading from cache as fallback
        const cachedData = this.loadFromCache();
        if (cachedData && cachedData.length > 0) {
          this.airports = cachedData;
          this.isLoaded = true;
          console.log(`✅ Loaded ${this.airports.length} airports from cache (fallback)`);
        }
      } finally {
        this.isLoading = false;
      }
    }
  
    /**
     * Fetch airport data from OpenFlights GitHub
     */
    private async fetchFromNetwork(): Promise<void> {
      try {
        console.log('🌐 Fetching airports from OpenFlights...');
  
        const response = await fetch(this.DATA_URL, {
          method: 'GET',
          headers: {
            'Accept': 'text/plain',
          },
        });
  
        if (!response.ok) {
          throw new Error(`Failed to fetch airports: ${response.status}`);
        }
  
        const data = await response.text();
        const lines = data.split('\n');
        const airports: Airport[] = [];
  
        for (const line of lines) {
          if (!line.trim()) continue;
  
          try {
            const airport = this.parseAirportFromCsv(line);
  
            // Only include airports with valid IATA codes
            if (
              airport.iataCode &&
              airport.iataCode !== '\\N' &&
              airport.iataCode.length === 3
            ) {
              airports.push(airport);
            }
          } catch (e) {
            // Skip malformed lines
            continue;
          }
        }
  
        this.airports = airports;
        this.isLoaded = true;
  
        // Save to cache
        this.saveToCache(airports);
  
        console.log(`✅ Loaded ${airports.length} airports from network`);
  
      } catch (e) {
        console.error('❌ Network fetch failed:', e);
        throw e;
      }
    }
  
    /**
     * Parse airport from CSV line
     * CSV format: id,name,city,country,iata,icao,lat,lon,altitude,timezone_offset,dst,timezone,type,source
     */
    private parseAirportFromCsv(line: string): Airport {
      // Handle quoted strings properly
      const parts: string[] = [];
      let current = '';
      let inQuotes = false;
  
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
  
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          parts.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      parts.push(current.trim()); // Add last part
  
      const cleanString = (s: string): string => s.replace(/"/g, '').trim();
  
      return {
        id: parseInt(cleanString(parts[0])) || 0,
        name: cleanString(parts[1]) || '',
        city: cleanString(parts[2]) || '',
        country: cleanString(parts[3]) || '',
        iataCode: cleanString(parts[4]) || '',
        icaoCode: cleanString(parts[5]) || '',
        latitude: parseFloat(cleanString(parts[6])) || 0,
        longitude: parseFloat(cleanString(parts[7])) || 0,
        timezone: parts.length > 11 ? cleanString(parts[11]) : '',
      };
    }
  
    /**
     * Load airports from localStorage cache
     */
    private loadFromCache(): Airport[] | null {
      try {
        // Check cache timestamp
        const timestampStr = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
        if (!timestampStr) return null;
  
        const timestamp = parseInt(timestampStr);
        const cacheAge = Date.now() - timestamp;
        const maxAge = this.CACHE_DURATION_DAYS * 24 * 60 * 60 * 1000;
  
        if (cacheAge > maxAge) {
          console.log('📦 Cache expired, will refresh from network');
          return null;
        }
  
        const jsonString = localStorage.getItem(this.CACHE_KEY);
        if (!jsonString) return null;
  
        const airports: Airport[] = JSON.parse(jsonString);
        return airports;
  
      } catch (e) {
        console.error('❌ Error loading from cache:', e);
        return null;
      }
    }
  
    /**
     * Save airports to localStorage cache
     */
    private saveToCache(airports: Airport[]): void {
      try {
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(airports));
        localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
        console.log(`💾 Saved ${airports.length} airports to cache`);
      } catch (e) {
        console.error('❌ Error saving to cache:', e);
        // If localStorage is full, try to clear old data
        try {
          localStorage.removeItem(this.CACHE_KEY);
          localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
        } catch (clearError) {
          console.error('❌ Error clearing cache:', clearError);
        }
      }
    }
  
    // ============================================================================
    // SEARCH METHODS
    // ============================================================================
  
    /**
     * Search airports with fuzzy matching
     * Returns results sorted by relevance
     */
    searchAirports(query: string, limit = 20): Airport[] {
      if (!this.isLoaded || !query) return [];
  
      const searchTerm = query.toLowerCase().trim();
      if (searchTerm.length < 2) return [];
  
      const results = this.airports.filter((airport) => {
        return (
          airport.name.toLowerCase().includes(searchTerm) ||
          airport.city.toLowerCase().includes(searchTerm) ||
          airport.country.toLowerCase().includes(searchTerm) ||
          airport.iataCode.toLowerCase().includes(searchTerm) ||
          airport.icaoCode.toLowerCase().includes(searchTerm)
        );
      });
  
      // Sort by relevance score
      results.sort((a, b) => {
        const scoreA = this.getRelevanceScore(a, searchTerm);
        const scoreB = this.getRelevanceScore(b, searchTerm);
        return scoreB - scoreA;
      });
  
      return results.slice(0, limit);
    }
  
    /**
     * Calculate relevance score for search results
     */
    private getRelevanceScore(airport: Airport, searchTerm: string): number {
      let score = 0;
  
      // Exact IATA code match - highest priority
      if (airport.iataCode.toLowerCase() === searchTerm) {
        score += 1000;
      }
  
      // IATA code starts with search term
      if (airport.iataCode.toLowerCase().startsWith(searchTerm)) {
        score += 500;
      }
  
      // City name starts with search term
      if (airport.city.toLowerCase().startsWith(searchTerm)) {
        score += 300;
      }
  
      // City name contains search term
      if (airport.city.toLowerCase().includes(searchTerm)) {
        score += 100;
      }
  
      // Airport name starts with search term
      if (airport.name.toLowerCase().startsWith(searchTerm)) {
        score += 200;
      }
  
      // Airport name contains search term
      if (airport.name.toLowerCase().includes(searchTerm)) {
        score += 50;
      }
  
      // Country contains search term
      if (airport.country.toLowerCase().includes(searchTerm)) {
        score += 25;
      }
  
      // Boost for popular airports
      if (POPULAR_DESTINATION_CODES.includes(airport.iataCode)) {
        score += 150;
      }
  
      // Boost for UAE airports
      if (UAE_AIRPORT_CODES.includes(airport.iataCode)) {
        score += 200;
      }
  
      // Boost for GCC airports
      if (GCC_AIRPORT_CODES.includes(airport.iataCode)) {
        score += 100;
      }
  
      return score;
    }
  
    // ============================================================================
    // SPECIALIZED GETTERS
    // ============================================================================
  
    /**
     * Get UAE airports (for departure selection)
     */
    getUaeAirports(): Airport[] {
      if (!this.isLoaded) return [];
  
      const uaeAirports = this.airports.filter((a) =>
        UAE_AIRPORT_CODES.includes(a.iataCode)
      );
  
      // Sort by priority order
      uaeAirports.sort((a, b) => {
        const indexA = UAE_AIRPORT_CODES.indexOf(a.iataCode);
        const indexB = UAE_AIRPORT_CODES.indexOf(b.iataCode);
        return indexA - indexB;
      });
  
      return uaeAirports;
    }
  
    /**
     * Get GCC airports
     */
    getGccAirports(): Airport[] {
      if (!this.isLoaded) return [];
  
      const gccAirports = this.airports.filter((a) =>
        GCC_AIRPORT_CODES.includes(a.iataCode)
      );
  
      gccAirports.sort((a, b) => a.city.localeCompare(b.city));
      return gccAirports;
    }
  
    /**
     * Get popular destination airports
     */
    getPopularDestinations(): Airport[] {
      if (!this.isLoaded) return [];
  
      const popular = this.airports.filter((a) =>
        POPULAR_DESTINATION_CODES.includes(a.iataCode)
      );
  
      // Sort by priority order
      popular.sort((a, b) => {
        const indexA = POPULAR_DESTINATION_CODES.indexOf(a.iataCode);
        const indexB = POPULAR_DESTINATION_CODES.indexOf(b.iataCode);
        return indexA - indexB;
      });
  
      return popular;
    }
  
    /**
     * Get airport by IATA code
     */
    getAirportByCode(iataCode: string): Airport | undefined {
      if (!this.isLoaded || !iataCode) return undefined;
  
      return this.airports.find(
        (a) => a.iataCode.toUpperCase() === iataCode.toUpperCase()
      );
    }
  
    /**
     * Get airports by country
     */
    getAirportsByCountry(country: string): Airport[] {
      if (!this.isLoaded || !country) return [];
  
      const airports = this.airports.filter(
        (a) => a.country.toLowerCase() === country.toLowerCase()
      );
  
      airports.sort((a, b) => a.city.localeCompare(b.city));
      return airports;
    }
  
    /**
     * Get all unique countries
     */
    getAllCountries(): string[] {
      if (!this.isLoaded) return [];
  
      const countries = [...new Set(this.airports.map((a) => a.country))];
      countries.sort();
      return countries;
    }
  
    /**
     * Get airports grouped by country
     */
    getAirportsGroupedByCountry(): Record<string, Airport[]> {
      if (!this.isLoaded) return {};
  
      const grouped: Record<string, Airport[]> = {};
  
      for (const airport of this.airports) {
        if (!grouped[airport.country]) {
          grouped[airport.country] = [];
        }
        grouped[airport.country].push(airport);
      }
  
      // Sort airports within each country by city
      for (const country of Object.keys(grouped)) {
        grouped[country].sort((a, b) => a.city.localeCompare(b.city));
      }
  
      return grouped;
    }
  
    // ============================================================================
    // UTILITY METHODS
    // ============================================================================
  
    /**
     * Check if data is loaded
     */
    get loaded(): boolean {
      return this.isLoaded;
    }
  
    /**
     * Check if currently loading
     */
    get loading(): boolean {
      return this.isLoading;
    }
  
    /**
     * Get total airport count
     */
    get airportCount(): number {
      return this.airports.length;
    }
  
    /**
     * Get all airports
     */
    getAllAirports(): Airport[] {
      return [...this.airports];
    }
  
    /**
     * Clear all cached data
     */
    clearCache(): void {
      try {
        localStorage.removeItem(this.CACHE_KEY);
        localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
        this.airports = [];
        this.isLoaded = false;
        console.log('🗑️ Airport cache cleared');
      } catch (e) {
        console.error('❌ Error clearing cache:', e);
      }
    }
  
    /**
     * Force refresh from network
     */
    async refreshFromNetwork(): Promise<void> {
      await this.loadAirports(true);
    }
  }
  
  // Export singleton instance
  export const airportService = new AirportService();
  export default airportService;